import type {
  ProgramSubject,
  SubjectDetailItem,
  SubjectLandingData,
} from '../types/subjects'
import { paths } from '../utils/paths'

// Every subject currently shares the same hero detail row.
const SUBJECT_DETAILS: SubjectDetailItem[] = [
  { label: 'Start Date', value: 'July 7, 2026', kind: 'startDate' },
  { label: 'Selective Cohort', value: 'Limited seats', kind: 'cohort' },
  { label: 'Format', value: 'AI-personalized', kind: 'format' },
  {
    label: 'Time Commitment',
    value: 'Self-paced (~10hrs/wk)',
    kind: 'duration',
  },
]

const sharedProgramSubjects: ProgramSubject[] = [
  {
    name: 'AI For Leaders',
    tokenSlug: 'ai-for-leaders',
    pageSlug: 'ai-for-leaders',
  },
  {
    name: 'Finance And Data Intelligence',
    tokenSlug: 'finance-and-data-intelligence',
    pageSlug: 'finance-data-intelligence',
  },
  {
    name: 'Product Innovation',
    tokenSlug: 'product-innovation',
    pageSlug: 'product-innovation',
  },
  {
    name: 'High Output Thinking',
    tokenSlug: 'high-output-thinking',
    pageSlug: 'high-output-thinking',
  },
  {
    name: 'Power & Influence',
    tokenSlug: 'power-and-influence',
    pageSlug: 'power-influence',
  },
  {
    name: 'Career Power',
    tokenSlug: 'career-power',
    pageSlug: 'career-power',
  },
  {
    name: 'Leadership Principles',
    tokenSlug: 'leadership-principles',
    pageSlug: 'leadership-principles',
  },
  {
    name: 'Modern Marketing & Sales',
    tokenSlug: 'modern-marketing',
    pageSlug: 'modern-marketing',
  },
  {
    name: 'Strategy & Operations',
    tokenSlug: 'strategy-and-operations',
    pageSlug: 'strategy-operations',
  },
]

const subjectDataBySlug: Record<string, SubjectLandingData> = {
  'ai-for-leaders': {
    hero: {
      categoryLabel: 'AI For Leaders',
      headline: 'Use AI to lead smarter and move faster.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-ai-for-leaders-hero.jpg',
      videoUrl: 'https://static.masterclass.com/ai-for-leaders.mp4',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'alex-imas',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-alex-imas.jpg',
        name: 'Alex Imas',
        bio: 'Chicago Booth professor and behavioral economist; coauthored a book with Nobel Prize–winning economist Richard Thaler',
        isChicago: true,
      },
      {
        id: 'bob-sutton',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-bob-sutton.jpg',
        name: 'Bob Sutton',
        bio: 'Stanford professor and author of The No Asshole Rule',
      },
      {
        id: 'gary-vaynerchuk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-gary-vaynerchuk.jpg',
        name: 'Gary Vaynerchuk',
        bio: 'Chairman of VaynerX and CEO of VaynerMedia',
      },
      {
        id: 'grant-lee',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-grant-lee.jpg',
        name: 'Grant Lee',
        bio: 'CEO and Co-Founder, Gamma',
      },
      {
        id: 'indra-nooyi',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-indra-nooyi-v2.jpg',
        name: 'Indra Nooyi',
        bio: 'Former CEO of PepsiCo and Fortune 50 global business leader',
      },
      {
        id: 'jp-dube',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jean-pierre-dube-2.jpg',
        name: 'Jean-Pierre Dubé',
        bio: 'Chicago Booth professor and researcher in pricing, consumer behavior, and marketing strategy',
        isChicago: true,
      },
      {
        id: 'mark-cuban',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mark-cuban.jpg',
        name: 'Mark Cuban',
        bio: 'Entrepreneur and investor building companies across tech, media, and healthcare',
      },
      {
        id: 'mati-staniszewski',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mati-staniszewski.jpg',
        name: 'Mati Staniszewski',
        bio: 'Co-Founder, ElevenLabs',
      },
      {
        id: 'ray-dalio',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ray-dalio.jpg',
        name: 'Ray Dalio',
        bio: 'Founder of Bridgewater Associates and author of Principles',
      },
      {
        id: 'sanjog-misra',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-sanjog-misra.jpg',
        name: 'Sanjog Misra',
        bio: 'Chicago Booth professor pioneering AI and machine learning in consumer and business analytics',
        isChicago: true,
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    programSubjects: sharedProgramSubjects,
    outcomes: [
      {
        id: 'leverage-ai',
        description: 'Leverage AI to enhance your leadership skills',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ai-applications.jpg',
      },
      {
        id: 'ai-confidence',
        description:
          'Spot where AI creates real value in your business versus where it could lead to operational risk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ai-value.png',
      },
      {
        id: 'ai-ethics',
        description:
          'Navigate the changing AI landscape and be the leader your team needs',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-legal-and-ethical.jpg',
      },
      {
        id: 'ai-leadership',
        description:
          'Rethink how your team is structured and put AI agents to work',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-build-buy-configure.jpg',
      },
      {
        id: 'ai-tools',
        description: 'Make sharper strategic bets and more confident decisions',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-lead-team.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'How AI Actually Works',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'AI Organizational Readiness',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Turn AI Into Business Impact',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Human-Agent Team Design',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'AI-Augmented Strategy',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Stay Ahead with AI',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Leading the AI Transition',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 8,
        title: 'AI Adoption and Upskilling',
        description: '',
        topics: [],
      },
    ],
  },
  'product-innovation': {
    hero: {
      categoryLabel: 'Product Innovation',
      headline: 'Turn ideas into products that scale.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-product-innovation-hero.jpg',
      videoUrl:
        'https://static.masterclass.com/educator-subjects-product-inovation.mp4',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'bob-sutton',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-bob-sutton.jpg',
        name: 'Bob Sutton',
        bio: 'Stanford professor and author of The No Asshole Rule',
      },
      {
        id: 'gary-vaynerchuk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-gary-vaynerchuk.jpg',
        name: 'Gary Vaynerchuk',
        bio: 'Chairman of VaynerX and CEO of VaynerMedia',
      },
      {
        id: 'grant-lee',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-grant-lee.jpg',
        name: 'Grant Lee',
        bio: 'CEO and Co-Founder, Gamma',
      },
      {
        id: 'issa-rae',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-issa-rae-2.jpg',
        name: 'Issa Rae',
        bio: 'Emmy-nominated creator of Insecure and founder of HOORAE',
      },
      {
        id: 'jack-dongarra',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jack-dongarra.jpg',
        name: 'Jack Dongarra',
        bio: 'Turing Award–winning mathematician in high-performance computing',
      },
      {
        id: 'jp-dube',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jean-pierre-dube-2.jpg',
        name: 'Jean-Pierre Dubé',
        bio: 'Chicago Booth professor and researcher in pricing, consumer behavior, and marketing strategy',
        isChicago: true,
      },
      {
        id: 'mark-cuban',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mark-cuban.jpg',
        name: 'Mark Cuban',
        bio: 'Entrepreneur and investor building companies across tech, media, and healthcare',
      },
      {
        id: 'mati-staniszewski',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mati-staniszewski.jpg',
        name: 'Mati Staniszewski',
        bio: 'Co-Founder, ElevenLabs',
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'ai-product-concepts',
        description:
          'Use AI to generate and rapidly test product concepts before you commit and build.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-problem-solving.jpg',
      },
      {
        id: 'product-strategy',
        description:
          'Set a product strategy with real trade-offs, not just a feature list.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-product-stragety.jpg',
      },
      {
        id: 'test-assumptions',
        description:
          'Test your riskiest assumptions before committing to build anything.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-test-risk.jpg',
      },
      {
        id: 'product-metrics',
        description:
          'Use the right metrics to know if your product has real traction.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-metrics.jpg',
      },
      {
        id: 'habits-engagement',
        description:
          'Design for the habits and engagement that keep users coming back.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-habit-design.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: "Product Thinking for Today's Leader",
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Identify & Solve the Right Problems',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Innovation to Market',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Build Products People Want',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Speak Design and Engineering',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Build AI-Powered Products',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Make Better Product Decisions with Data',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 8,
        title: 'Build For Growth',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'finance-data-intelligence': {
    hero: {
      categoryLabel: 'Finance and Data Intelligence',
      headline: 'The Financial Skills Every Leader Needs Now.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-finance-and-data-hero.jpg',
      videoUrl:
        'https://static.masterclass.com/educator-subjects-finance-and-data-intelligence.mp4',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'anil-kashyap',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-anil-kashyap.jpg',
        name: 'Anil Kashyap',
        bio: 'Chicago Booth professor and global authority on banking and monetary policy; advisor to the Fed and Bank of England',
        isChicago: true,
      },
      {
        id: 'doug-diamond',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-douglas-diamond.jpg',
        name: 'Doug Diamond',
        bio: 'Nobel Prize–winning economist and Chicago Booth professor; pioneer of modern banking theory',
        isChicago: true,
      },
      {
        id: 'kevin-mayer',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-kevin-mayer.jpg',
        name: 'Kevin Mayer',
        bio: 'Former CEO of TikTok and architect of Disney+',
      },
      {
        id: 'linda-yueh',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-linda-yueh.jpg',
        name: 'Linda Yueh',
        bio: 'Oxford economist and author',
      },
      {
        id: 'mark-cuban',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mark-cuban.jpg',
        name: 'Mark Cuban',
        bio: 'Entrepreneur and investor building companies across tech, media, and healthcare',
      },
      {
        id: 'paul-krugman',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-paul-krugman-2.jpg',
        name: 'Paul Krugman',
        bio: 'Nobel Prize–winning economist and longtime New York Times columnist',
      },
      {
        id: 'ray-dalio',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ray-dalio.jpg',
        name: 'Ray Dalio',
        bio: 'Founder of Bridgewater Associates and author of Principles',
      },
      {
        id: 'steven-kaplan',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-steven-kaplan.jpg',
        name: 'Steven Kaplan',
        bio: 'Chicago Booth professor of entrepreneurship and finance; leading scholar in private equity',
        isChicago: true,
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'ai-data-decisions',
        description:
          'Put AI and data to work for faster, sharper financial decisions.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-financial-conversation.jpg',
      },
      {
        id: 'market-forces',
        description:
          'Anticipate and understand the forces that drive market shifts.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-avoid-crisis.jpg',
      },
      {
        id: 'cash-flow',
        description:
          'Master cash flow management and stay ahead of financial challenges.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-cashflow-and-profit.jpg',
      },
      {
        id: 'strategic-investments',
        description:
          'Make investments strategically and build plans that actually drive results.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-financial-case.jpg',
      },
      {
        id: 'financial-statements',
        description:
          'Read and understand financial statements to know exactly what to do next.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-read-p-and-l.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'Decoding Business Performance',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Shape & Predict Outcomes',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Control Your Cash',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Make Smarter Bets',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Planning Under Pressure',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Data for Decision Makers',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'AI-informed Financial Decisions',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 8,
        title: 'Growth Stage Finance',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'career-power': {
    hero: {
      categoryLabel: 'Career Power',
      headline: 'Take control of where your career goes next.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-career-power-hero.jpg',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'alex-imas',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-alex-imas.jpg',
        name: 'Alex Imas',
        bio: 'Chicago Booth professor and behavioral economist; coauthored a book with Nobel Prize–winning economist Richard Thaler',
        isChicago: true,
      },
      {
        id: 'anil-kashyap',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-anil-kashyap.jpg',
        name: 'Anil Kashyap',
        bio: 'Chicago Booth professor and global authority on banking and monetary policy; advisor to the Fed and Bank of England',
        isChicago: true,
      },
      {
        id: 'dambisa-moyo',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-baroness-dambisa-moyo-2.jpg',
        name: 'Dambisa Moyo',
        bio: 'Global Economist, Author & Board Member',
      },
      {
        id: 'indra-nooyi',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-indra-nooyi-v2.jpg',
        name: 'Indra Nooyi',
        bio: 'Former CEO of PepsiCo and Fortune 50 global business leader',
      },
      {
        id: 'issa-rae',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-issa-rae-2.jpg',
        name: 'Issa Rae',
        bio: 'Emmy-nominated creator of Insecure and founder of HOORAE',
      },
      {
        id: 'jack-dongarra',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jack-dongarra.jpg',
        name: 'Jack Dongarra',
        bio: 'Turing Award–winning mathematician in high-performance computing',
      },
      {
        id: 'margaret-ann-neale',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-margaret-ann-neale-2.jpg',
        name: 'Margaret Ann Neale',
        bio: 'Stanford GSB professor and expert in negotiation and influence',
      },
      {
        id: 'maureen-chiquet',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-maureen-chiquet-2.jpg',
        name: 'Maureen Chiquet',
        bio: 'Former Global CEO of Chanel and board director at global consumer brands',
      },
      {
        id: 'nicholas-epley',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-nicholas-epley.jpg',
        name: 'Nicholas Epley',
        bio: 'Chicago Booth professor and author of Mindwise; social psychologist studying human insight',
        isChicago: true,
      },
      {
        id: 'steven-kaplan',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-steven-kaplan.jpg',
        name: 'Steven Kaplan',
        bio: 'Chicago Booth professor of entrepreneurship and finance; leading scholar in private equity',
        isChicago: true,
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'career-compounds',
        description: 'Build a career that compounds, not just reacts.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-career-power-outcome-1.jpg',
      },
      {
        id: 'ai-era-value',
        description: 'Demonstrate your skills and value in the AI Era.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-career-power-outcome-2.jpg',
      },
      {
        id: 'corporate-hierarchy',
        description: 'Navigate the new corporate hierarchy.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-career-power-outcome-3.jpg',
      },
      {
        id: 'leading-candidate',
        description:
          'Position yourself as a leading candidate before the role even opens.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-career-power-outcome-4.jpg',
      },
      {
        id: 'negotiate-offer',
        description:
          'Negotiate your next offer with a clear strategy—not guesswork.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-career-power-outcome-5.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'Map Your Career Path',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Honing Your Leadership Skillset',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'The Mechanics of Promotion',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Building Your Personal Brand',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Build a Network That Opens Doors',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Master the Hiring Process',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Use AI to Accelerate Your Career',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'leadership-principles': {
    hero: {
      categoryLabel: 'Leadership Principles',
      headline: 'Become the leader your team needs today.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-leadership-principles-hero.jpg',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'alex-imas',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-alex-imas.jpg',
        name: 'Alex Imas',
        bio: 'Chicago Booth professor and behavioral economist; coauthored a book with Nobel Prize–winning economist Richard Thaler',
        isChicago: true,
      },
      {
        id: 'ayelet-fishbach',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ayelet-fishbach-2.jpg',
        name: 'Ayelet Fishbach',
        bio: 'Chicago Booth professor and author of Get It Done; expert on human motivation and behavior',
        isChicago: true,
      },
      {
        id: 'bob-sutton',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-bob-sutton.jpg',
        name: 'Bob Sutton',
        bio: 'Stanford professor and author of The No Asshole Rule',
      },
      {
        id: 'dambisa-moyo',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-baroness-dambisa-moyo-2.jpg',
        name: 'Dambisa Moyo',
        bio: 'Global Economist, Author & Board Member',
      },
      {
        id: 'indra-nooyi',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-indra-nooyi-v2.jpg',
        name: 'Indra Nooyi',
        bio: 'Former CEO of PepsiCo and Fortune 50 global business leader',
      },
      {
        id: 'issa-rae',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-issa-rae-2.jpg',
        name: 'Issa Rae',
        bio: 'Emmy-nominated creator of Insecure and founder of HOORAE',
      },
      {
        id: 'mark-cuban',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mark-cuban.jpg',
        name: 'Mark Cuban',
        bio: 'Entrepreneur and investor building companies across tech, media, and healthcare',
      },
      {
        id: 'mati-staniszewski',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mati-staniszewski.jpg',
        name: 'Mati Staniszewski',
        bio: 'Co-Founder, ElevenLabs',
      },
      {
        id: 'maureen-chiquet',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-maureen-chiquet-2.jpg',
        name: 'Maureen Chiquet',
        bio: 'Former Global CEO of Chanel and board director at global consumer brands',
      },
      {
        id: 'michael-bungay-stanier',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-michael-bungay-stanier-2.jpg',
        name: 'Michael Bungay Stanier',
        bio: 'Founder of Box of Crayons and author of The Coaching Habit',
      },
      {
        id: 'ray-dalio',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ray-dalio.jpg',
        name: 'Ray Dalio',
        bio: 'Founder of Bridgewater Associates and author of Principles',
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'leadership-philosophy',
        description: 'Define and implement your leadership philosophy.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-leadership-principles-outcome-1.jpg',
      },
      {
        id: 'ai-strategic-decisions',
        description: 'Use AI to evaluate options and make strategic decisions.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-leadership-principles-outcome-2.jpg',
      },
      {
        id: 'intentional-culture',
        description:
          'Build an intentional company culture — and fix it when it breaks.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-leadership-principles-outcome-3.jpg',
      },
      {
        id: 'lead-through-change',
        description:
          'Lead with confidence and maintain trust through continuous change.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-leadership-principles-outcome-4.jpg',
      },
      {
        id: 'manage-up-down-across',
        description:
          'Manage up, down, and across with influence and authority.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-leadership-principles-outcome-5.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'Developing Your Leadership Ethos',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Design an Intentional Culture',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Decision Making and Judgement',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Strategic Approaches to Change',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Structuring Organizations for Scale',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Hiring, Firing, and Development',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'The Coaching Mindset for Leaders',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 8,
        title: 'The Politics of Modern Leadership',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 9,
        title: 'Leading in the AI-Era',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'high-output-thinking': {
    hero: {
      categoryLabel: 'High Output Thinking',
      headline: 'Do more of the work that actually matters.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-high-output-thinking-hero.jpg',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'alex-imas',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-alex-imas.jpg',
        name: 'Alex Imas',
        bio: 'Chicago Booth professor and behavioral economist; coauthored a book with Nobel Prize–winning economist Richard Thaler',
        isChicago: true,
      },
      {
        id: 'anil-kashyap',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-anil-kashyap.jpg',
        name: 'Anil Kashyap',
        bio: 'Chicago Booth professor and global authority on banking and monetary policy; advisor to the Fed and Bank of England',
        isChicago: true,
      },
      {
        id: 'ayelet-fishbach',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ayelet-fishbach-2.jpg',
        name: 'Ayelet Fishbach',
        bio: 'Chicago Booth professor and author of Get It Done; expert on human motivation and behavior',
        isChicago: true,
      },
      {
        id: 'eugene-fama',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-eugene-fama.jpg',
        name: 'Eugene Fama',
        bio: 'Nobel Prize–winning economist and Chicago Booth professor; pioneer of modern finance',
        missingImage: true,
      },
      {
        id: 'gary-vaynerchuk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-gary-vaynerchuk.jpg',
        name: 'Gary Vaynerchuk',
        bio: 'Chairman of VaynerX and CEO of VaynerMedia',
      },
      {
        id: 'grant-lee',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-grant-lee.jpg',
        name: 'Grant Lee',
        bio: 'CEO and Co-Founder, Gamma',
      },
      {
        id: 'issa-rae',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-issa-rae-2.jpg',
        name: 'Issa Rae',
        bio: 'Emmy-nominated creator of Insecure and founder of HOORAE',
      },
      {
        id: 'jack-dongarra',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jack-dongarra.jpg',
        name: 'Jack Dongarra',
        bio: 'Turing Award–winning mathematician in high-performance computing',
      },
      {
        id: 'mark-cuban',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mark-cuban.jpg',
        name: 'Mark Cuban',
        bio: 'Entrepreneur and investor building companies across tech, media, and healthcare',
      },
      {
        id: 'michael-bungay-stanier',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-michael-bungay-stanier-2.jpg',
        name: 'Michael Bungay Stanier',
        bio: 'Founder of Box of Crayons and author of The Coaching Habit',
      },
      {
        id: 'nicholas-epley',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-nicholas-epley.jpg',
        name: 'Nicholas Epley',
        bio: 'Chicago Booth professor and author of Mindwise; social psychologist studying human insight',
        isChicago: true,
      },
      {
        id: 'ray-dalio',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ray-dalio.jpg',
        name: 'Ray Dalio',
        bio: 'Founder of Bridgewater Associates and author of Principles',
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'learn-faster',
        description:
          'Learn any new skill faster with a proven system, not just effort.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-high-output-thinking-outcome-1.jpg',
      },
      {
        id: 'ai-knowledge-system',
        description:
          'Build a personal AI knowledge system that compounds over time.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-high-output-thinking-outcome-2.jpg',
      },
      {
        id: 'pareto-focus',
        description:
          'Focus on the 20% of work that drives 80% of your results.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-high-output-thinking-outcome-3.jpg',
      },
      {
        id: 'feedback-loops',
        description:
          'Design feedback loops that help you improve — without waiting for input.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-high-output-thinking-outcome-4.jpg',
      },
      {
        id: 'ai-skill-stack',
        description:
          'Develop an AI-powered skill stack for faster, better decisions.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-high-output-thinking-outcome-5.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'Be the Fastest Learner in the Room',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Personal Productivity in the AI-Era',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Solution-Based Thinking',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Creative Problem Solving',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'High-Stakes Decision Making',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Risk Assessment and Strategic Bets',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Building Trust as a Leader',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'strategy-operations': {
    hero: {
      categoryLabel: 'Strategy & Operations',
      headline: 'Bridge the gap between strategy and execution.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-strategy-and-operations-hero.jpg',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'anil-kashyap',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-anil-kashyap.jpg',
        name: 'Anil Kashyap',
        bio: 'Chicago Booth professor and global authority on banking and monetary policy; advisor to the Fed and Bank of England',
        isChicago: true,
      },
      {
        id: 'ayelet-fishbach',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ayelet-fishbach-2.jpg',
        name: 'Ayelet Fishbach',
        bio: 'Chicago Booth professor and author of Get It Done; expert on human motivation and behavior',
        isChicago: true,
      },
      {
        id: 'dambisa-moyo',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-baroness-dambisa-moyo-2.jpg',
        name: 'Dambisa Moyo',
        bio: 'Global Economist, Author & Board Member',
      },
      {
        id: 'doug-diamond',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-douglas-diamond.jpg',
        name: 'Doug Diamond',
        bio: 'Nobel Prize–winning economist and Chicago Booth professor; pioneer of modern banking theory',
        isChicago: true,
      },
      {
        id: 'gary-vaynerchuk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-gary-vaynerchuk.jpg',
        name: 'Gary Vaynerchuk',
        bio: 'Chairman of VaynerX and CEO of VaynerMedia',
      },
      {
        id: 'grant-lee',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-grant-lee.jpg',
        name: 'Grant Lee',
        bio: 'CEO and Co-Founder, Gamma',
      },
      {
        id: 'indra-nooyi',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-indra-nooyi-v2.jpg',
        name: 'Indra Nooyi',
        bio: 'Former CEO of PepsiCo and Fortune 50 global business leader',
      },
      {
        id: 'issa-rae',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-issa-rae-2.jpg',
        name: 'Issa Rae',
        bio: 'Emmy-nominated creator of Insecure and founder of HOORAE',
      },
      {
        id: 'jack-dongarra',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jack-dongarra.jpg',
        name: 'Jack Dongarra',
        bio: 'Turing Award–winning mathematician in high-performance computing',
      },
      {
        id: 'jp-dube',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-jean-pierre-dube-2.jpg',
        name: 'Jean-Pierre Dubé',
        bio: 'Chicago Booth professor and researcher in pricing, consumer behavior, and marketing strategy',
        isChicago: true,
      },
      {
        id: 'kevin-mayer',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-kevin-mayer.jpg',
        name: 'Kevin Mayer',
        bio: 'Former CEO of TikTok and architect of Disney+',
      },
      {
        id: 'linda-yueh',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-linda-yueh.jpg',
        name: 'Linda Yueh',
        bio: 'Oxford economist and author',
      },
      {
        id: 'paul-krugman',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-paul-krugman-2.jpg',
        name: 'Paul Krugman',
        bio: 'Nobel Prize–winning economist and longtime New York Times columnist',
      },
      {
        id: 'ray-dalio',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ray-dalio.jpg',
        name: 'Ray Dalio',
        bio: 'Founder of Bridgewater Associates and author of Principles',
      },
      {
        id: 'sanjog-misra',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-sanjog-misra.jpg',
        name: 'Sanjog Misra',
        bio: 'Chicago Booth professor pioneering AI and machine learning in consumer and business analytics',
        isChicago: true,
      },
      {
        id: 'steven-kaplan',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-steven-kaplan.jpg',
        name: 'Steven Kaplan',
        bio: 'Chicago Booth professor of entrepreneurship and finance; leading scholar in private equity',
        isChicago: true,
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'ai-pressure-test',
        description:
          'Use AI to sharpen your analysis and pressure-test your strategic bets.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-strategy-and-operations-outcome-1.jpg',
      },
      {
        id: 'strategy-vs-goals',
        description:
          'Tell the difference between real strategy and a list of goals.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-strategy-and-operations-outcome-2.jpg',
      },
      {
        id: 'strategy-traps',
        description:
          'Understand why smart strategies fail — and how to avoid common traps.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-strategy-and-operations-outcome-3.jpg',
      },
      {
        id: 'operational-inefficiencies',
        description:
          'Spot the hidden operational inefficiencies holding your team back.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-strategy-and-operations-outcome-4.jpg',
      },
      {
        id: 'strategic-frameworks',
        description:
          'Apply strategic frameworks to evaluate challenges and opportunities.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-strategy-and-operations-outcome-5.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'Principles of Business Strategy',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Evaluating the Landscape',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Decoding Business Models',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Decide Where to Play and How to Win',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Selling Your Strategic Vision',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Effective Strategy Execution',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Ongoing Operational Effectiveness',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 8,
        title: 'Navigating Disruption',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'power-influence': {
    hero: {
      categoryLabel: 'Power & Influence',
      headline: 'Move people, teams, and outcomes.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-power-and-influence-hero.jpg',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'alex-imas',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-alex-imas.jpg',
        name: 'Alex Imas',
        bio: 'Chicago Booth professor and behavioral economist; coauthored a book with Nobel Prize–winning economist Richard Thaler',
        isChicago: true,
      },
      {
        id: 'ayelet-fishbach',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ayelet-fishbach-2.jpg',
        name: 'Ayelet Fishbach',
        bio: 'Chicago Booth professor and author of Get It Done; expert on human motivation and behavior',
        isChicago: true,
      },
      {
        id: 'bob-sutton',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-bob-sutton.jpg',
        name: 'Bob Sutton',
        bio: 'Stanford professor and author of The No Asshole Rule',
      },
      {
        id: 'dambisa-moyo',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-baroness-dambisa-moyo-2.jpg',
        name: 'Dambisa Moyo',
        bio: 'Global Economist, Author & Board Member',
      },
      {
        id: 'gary-vaynerchuk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-gary-vaynerchuk.jpg',
        name: 'Gary Vaynerchuk',
        bio: 'Chairman of VaynerX and CEO of VaynerMedia',
      },
      {
        id: 'hal-hershfield',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-hal-hershfield.jpg',
        name: 'Hal Hershfield',
        bio: 'UCLA professor and researcher on long-term decision-making',
      },
      {
        id: 'indra-nooyi',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-indra-nooyi-v2.jpg',
        name: 'Indra Nooyi',
        bio: 'Former CEO of PepsiCo and Fortune 50 global business leader',
      },
      {
        id: 'kevin-mayer',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-kevin-mayer.jpg',
        name: 'Kevin Mayer',
        bio: 'Former CEO of TikTok and architect of Disney+',
      },
      {
        id: 'margaret-ann-neale',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-margaret-ann-neale-2.jpg',
        name: 'Margaret Ann Neale',
        bio: 'Stanford GSB professor and expert in negotiation and influence',
      },
      {
        id: 'maureen-chiquet',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-maureen-chiquet-2.jpg',
        name: 'Maureen Chiquet',
        bio: 'Former Global CEO of Chanel and board director at global consumer brands',
      },
      {
        id: 'michael-bungay-stanier',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-michael-bungay-stanier-2.jpg',
        name: 'Michael Bungay Stanier',
        bio: 'Founder of Box of Crayons and author of The Coaching Habit',
      },
      {
        id: 'nicholas-epley',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-nicholas-epley.jpg',
        name: 'Nicholas Epley',
        bio: 'Chicago Booth professor and author of Mindwise; social psychologist studying human insight',
        isChicago: true,
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'self-awareness',
        description: 'See yourself the way others see you — and close the gap.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-power-and-influence-outcome-1.jpg',
      },
      {
        id: 'emotion-bias-influence',
        description:
          'Recognize how emotion and bias shape influence, including your own.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-power-and-influence-outcome-2.jpg',
      },
      {
        id: 'persuasive-communication',
        description: 'Communicate persuasively to win buy-in for your ideas.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-power-and-influence-outcome-3.jpg',
      },
      {
        id: 'read-the-room',
        description: 'Read the room in negotiations and adapt in real time.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-power-and-influence-outcome-4.jpg',
      },
      {
        id: 'map-power',
        description: 'Map power in your org and build alliances that matter.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-power-and-influence-outcome-5.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'The Psychology of Influence',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'The Self-Aware Leader',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Persuasive Communication',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Winning Strategies for Negotiation',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Principles of Organizational Power',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Cross-Functional Influence',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Owning Career-Defining Moments',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
  'modern-marketing': {
    hero: {
      categoryLabel: 'Modern Marketing & Sales',
      headline: 'Build demand. Close deals. Drive growth.',
      artImageUrl:
        'https://static.masterclass.com/educator-subjects-modern-marketing-hero.jpg',
      subjectIconColor: '#9FC9E0',
      ctaLabel: 'Apply for Free',
      ctaHref: paths.enrollmentApplication,
      promoText:
        'Apply to MasterClass Executive — 10 subjects, Chicago Booth and MasterClass, certificate of completion, and AI Labs for practical skills.',
      details: SUBJECT_DETAILS,
      partnerLogoAlt: 'Chicago Booth',
      finePrint: {
        text: 'This subject is part of',
        linkText: 'MasterClass Executive',
        linkHref: paths.programDetails,
      },
    },
    instructors: [
      {
        id: 'ayelet-fishbach',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-ayelet-fishbach-2.jpg',
        name: 'Ayelet Fishbach',
        bio: 'Chicago Booth professor and author of Get It Done; expert on human motivation and behavior',
        isChicago: true,
      },
      {
        id: 'gary-vaynerchuk',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-gary-vaynerchuk.jpg',
        name: 'Gary Vaynerchuk',
        bio: 'Chairman of VaynerX and CEO of VaynerMedia',
      },
      {
        id: 'grant-lee',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-grant-lee.jpg',
        name: 'Grant Lee',
        bio: 'CEO and Co-Founder, Gamma',
      },
      {
        id: 'hal-hershfield',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-hal-hershfield.jpg',
        name: 'Hal Hershfield',
        bio: 'UCLA professor and researcher on long-term decision-making',
      },
      {
        id: 'mark-cuban',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mark-cuban.jpg',
        name: 'Mark Cuban',
        bio: 'Entrepreneur and investor building companies across tech, media, and healthcare',
      },
      {
        id: 'mati-staniszewski',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-mati-staniszewski.jpg',
        name: 'Mati Staniszewski',
        bio: 'Co-Founder, ElevenLabs',
      },
      {
        id: 'maureen-chiquet',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-maureen-chiquet-2.jpg',
        name: 'Maureen Chiquet',
        bio: 'Former Global CEO of Chanel and board director at global consumer brands',
      },
      {
        id: 'nicholas-epley',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-nicholas-epley.jpg',
        name: 'Nicholas Epley',
        bio: 'Chicago Booth professor and author of Mindwise; social psychologist studying human insight',
        isChicago: true,
      },
    ],
    admissions: {
      ctaHref: paths.enrollmentApplication,
    },
    outcomes: [
      {
        id: 'ai-marketing-speed',
        description:
          'Use AI to speed up research, sharpen content, and personalize at scale.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-modern-marketing-outcome-1.jpg',
      },
      {
        id: 'funnel-optimization',
        description:
          'Optimize every stage of the funnel from awareness to close.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-modern-marketing-outcome-2.jpg',
      },
      {
        id: 'customer-buying-motivation',
        description:
          'Uncover why your customers actually buy — and use it to sell.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-modern-marketing-outcome-3.jpg',
      },
      {
        id: 'enduring-brand',
        description: 'Build a lasting brand that generates demand on its own.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-modern-marketing-outcome-4.jpg',
      },
      {
        id: 'consultative-selling',
        description:
          'Master consultative selling and build a sales team that scales.',
        imageUrl:
          'https://static.masterclass.com/educator-subjects-modern-marketing-outcome-5.jpg',
      },
    ],
    modules: [
      {
        moduleNumber: 1,
        title: 'Going to Market',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 2,
        title: 'Why Customers Actually Buy',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 3,
        title: 'Metrics That Matter',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 4,
        title: 'Building an Enduring Brand',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 5,
        title: 'Channels & Distribution',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 6,
        title: 'Compounding Growth',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 7,
        title: 'Marketing in the AI-Era',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 8,
        title: 'Modern Sales Foundations',
        description: '',
        topics: [],
      },
      {
        moduleNumber: 9,
        title: 'Designing the Sales Organization',
        description: '',
        topics: [],
      },
    ],
    programSubjects: sharedProgramSubjects,
  },
}

const getSubjectData = (slug: string): SubjectLandingData | null =>
  subjectDataBySlug[slug] ?? null

const getSubjectsList = (): { slug: string; name: string }[] =>
  sharedProgramSubjects
    .map((s) => {
      const data = subjectDataBySlug[s.pageSlug]
      return data ? { slug: s.pageSlug, name: data.hero.categoryLabel } : null
    })
    .filter((s): s is { slug: string; name: string } => s !== null)

const getNavSubjects = (): {
  slug: string
  name: string
  comingSoon?: boolean
}[] => {
  const activeSubjects = getSubjectsList()
  const activeSlugs = new Set(activeSubjects.map((s) => s.slug))
  const comingSoonSubjects = sharedProgramSubjects
    .filter((s) => s.comingSoon && !activeSlugs.has(s.pageSlug))
    .map((s) => ({ slug: s.pageSlug, name: s.name, comingSoon: true as const }))
  return [...activeSubjects, ...comingSoonSubjects]
}

/** Token slugs for all subjects — matches the [data-subject='...'] selectors
 *  in educator-design-system/src/styles/tokens/subjects.css. Use these as the
 *  canonical list anywhere a subject slug is needed outside of a live API response. */
export const SUBJECT_TOKEN_SLUGS = [
  'marketing',
  'ai-for-leaders',
  'product-innovation',
  'high-output-thinking',
  'power-and-influence',
  'career-power',
  'leadership-principles',
  'ai-foundations',
  'finance-and-data-intelligence',
  'strategy-and-operations',
  'brand',
] as const

/**
 * AI Foundations is a special subject: a single lesson of two parts (a video and
 * a quiz, 50% each) with no further lessons and no lesson artwork. Its
 * end-of-topic experience therefore skips the curriculum grid entirely and only
 * surfaces at completion (see the ExecutiveChat SubjectProgressCard). Sourced
 * from the canonical token-slug list above so it stays in lockstep with it.
 */
export const AI_FOUNDATIONS_SLUG: (typeof SUBJECT_TOKEN_SLUGS)[number] =
  'ai-foundations'

/**
 * Program subjects paired as canonical display name + token slug + page slug.
 * The **token slug** drives the `[data-subject='…']` tint (subject-square
 * colors, etc.); the **page slug** is the URL/API identifier (what
 * `LearningService.getSubjectDetail` expects). The two often differ
 * (e.g. `strategy-and-operations` vs `strategy-operations`). Use this where a
 * subject's name, tile tint, and curriculum must stay in lockstep (e.g. the
 * end-of-topic SubjectProgressCard).
 */
export const getProgramSubjectTiles = (): {
  name: string
  tokenSlug: string
  pageSlug: string
}[] =>
  sharedProgramSubjects.map(({ name, tokenSlug, pageSlug }) => ({
    name,
    tokenSlug,
    pageSlug,
  }))

/** The `[data-subject]` token for a URL/API subject slug; unknown slugs pass
 *  through (subjects.css keeps a default palette for them). */
export const subjectTokenSlug = (pageSlug: string): string =>
  sharedProgramSubjects.find((subject) => subject.pageSlug === pageSlug)
    ?.tokenSlug ?? pageSlug

export { getSubjectData, getSubjectsList, getNavSubjects }
